import { Alerta, Botao, Campo, Cartao, Entrada } from '@baiocchi/ui'
import { useNavigate } from 'react-router-dom'

import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { CabecalhoPagina } from '../../layout/layout-app'

/**
 * TELA-16 — Relatório de vendas.
 *
 * Os filtros existem e a tela está montada, mas o endpoint
 * `GET /indicadores/relatorio-vendas` **não foi implementado**, e o motivo é de
 * negócio, não de cronograma: o print do legado mostra apenas os filtros, então as
 * **colunas do relatório nunca foram vistas** (⚠️ Q9). Inventar colunas produziria
 * um relatório que ninguém pediu e que a operação teria de refazer.
 *
 * O que já está entregue e responde à maior parte das perguntas de vendas é o
 * **funil de conversão**, com agrupamento por unidade, equipe, responsável e ponto de
 * conversão — inclusive os filtros que o legado não tinha aqui.
 */
export function PaginaRelatorioVendas() {
  const { filtros, definir } = useFiltrosUrl()
  const navegar = useNavigate()

  return (
    <>
      <CabecalhoPagina
        titulo="Relatório de vendas"
        descricao="Vendas realizadas por período, unidade, equipe e responsável."
      />

      <Cartao className="mb-5">
        <div className="grid gap-3 sm:grid-cols-4">
          <Campo rotulo="Data início">
            {(atributos) => (
              <Entrada
                {...atributos}
                type="date"
                value={filtros.de ?? ''}
                onChange={(evento) => definir({ de: evento.target.value || undefined })}
              />
            )}
          </Campo>
          <Campo rotulo="Data fim">
            {(atributos) => (
              <Entrada
                {...atributos}
                type="date"
                value={filtros.ate ?? ''}
                onChange={(evento) => definir({ ate: evento.target.value || undefined })}
              />
            )}
          </Campo>
          <div className="flex items-end gap-2 sm:col-span-2">
            <Botao disabled>Gerar</Botao>
            <Botao variante="contorno" disabled>
              Exportar
            </Botao>
          </div>
        </div>
      </Cartao>

      <Alerta tom="atencao" titulo="Relatório aguardando definição de colunas">
        <p>
          O endpoint <code className="font-mono text-xs">GET /api/v1/indicadores/relatorio-vendas</code>{' '}
          está previsto no contrato, mas as colunas do relatório nunca foram definidas — a
          referência do sistema legado mostra apenas os filtros, com o relatório não gerado.
        </p>
        <p className="mt-2">
          A proposta em aberto é: lead, cliente, unidade, equipe, responsável, ponto de conversão,
          produto, data de chegada, data da venda, tempo até a venda e valor. Falta a confirmação de
          quem analisa vendas.
        </p>
        <p className="mt-2">
          Enquanto isso, o <strong>funil de conversão</strong> já responde volume, conversão e corte
          por unidade, equipe, responsável e ponto de conversão.
        </p>
        <div className="mt-3">
          <Botao tamanho="sm" onClick={() => navegar('/relatorios/funil')}>
            Abrir funil de conversão
          </Botao>
        </div>
      </Alerta>
    </>
  )
}

/**
 * TELA-17 — Relatório de propostas.
 *
 * Depende do módulo de propostas (TELA-07), que é Fase 2/3. Sem proposta criada, o
 * relatório contaria zero em todas as linhas.
 */
export function PaginaRelatorioPropostas() {
  const navegar = useNavigate()

  return (
    <>
      <CabecalhoPagina
        titulo="Relatório de propostas"
        descricao="Propostas geradas por período, equipe e unidade."
      />

      <Alerta tom="info" titulo="Depende do módulo de propostas (Fase 3)">
        <p>
          Nenhuma rota cria proposta hoje, então este relatório contaria zero em todas as linhas. A
          transição de lead para a situação <strong>Proposta</strong> já existe e exige uma proposta
          vinculada — ela só passa com dado importado.
        </p>
        <p className="mt-2">
          A ordem de dependência é: editor de proposta com sanitização no servidor (TELA-07) →
          versionamento → este relatório.
        </p>
        <div className="mt-3">
          <Botao variante="contorno" tamanho="sm" onClick={() => navegar('/relatorios/funil')}>
            Ver funil de conversão
          </Botao>
        </div>
      </Alerta>
    </>
  )
}
